import { useQuery } from "@tanstack/react-query";
import { apiClient, call } from "../../shared/api/client";
import type { RetrospectiveListKind } from "../../shared/api/types";

/** GRF-50 §9.7: the page refetches every 20 s while visible, and on focus, so two people writing see each other's entries. */
export const RETRO_REFRESH_MS = 20_000;

export const retrospectiveKey = (sprintId: number) => ["retrospective", sprintId] as const;

export function useRetrospective(sprintId: number, enabled = true) {
  return useQuery({
    queryKey: retrospectiveKey(sprintId),
    queryFn: () => call(apiClient.GET("/sprints/{sprintId}/retrospective", { params: { path: { sprintId } } })),
    enabled: enabled && sprintId > 0,
    refetchInterval: RETRO_REFRESH_MS,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
    retry: false,
  });
}

export function addEntry(sprintId: number, idempotencyKey: string, list: RetrospectiveListKind, text: string) {
  return call(
    apiClient.POST("/sprints/{sprintId}/retrospective/entries", {
      params: { path: { sprintId }, header: { "Idempotency-Key": idempotencyKey } },
      body: { list, text },
    }),
  );
}

export function editEntry(sprintId: number, entryId: number, version: number, text: string) {
  return call(
    apiClient.PATCH("/sprints/{sprintId}/retrospective/entries/{entryId}", {
      params: { path: { sprintId, entryId }, header: { "If-Match": String(version) } },
      body: { text },
    }),
  );
}

export function removeEntry(sprintId: number, entryId: number, version: number) {
  return call(
    apiClient.DELETE("/sprints/{sprintId}/retrospective/entries/{entryId}", {
      params: { path: { sprintId, entryId }, header: { "If-Match": String(version) } },
    }),
  );
}

export function createActionItem(
  sprintId: number,
  entryId: number,
  idempotencyKey: string,
  body: { summary: string; ownerId: number; dueDate: string | null },
) {
  return call(
    apiClient.POST("/sprints/{sprintId}/retrospective/entries/{entryId}/action-item", {
      params: { path: { sprintId, entryId }, header: { "Idempotency-Key": idempotencyKey } },
      body,
    }),
  );
}
